'use client';

import React, { useEffect, useRef } from 'react';
import type { FormikHelpers, FormikProps } from 'formik';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import clsx from 'clsx';
import { Input } from '@/components/input/Input';
import { Select } from '@/components/select/Select';
import { RadioGroup } from '@/components/radio-group/RadioGroup';
import { useTranslations } from '@/lib/locale-context';
import type { TerminProps } from '@/sections/forms/multistep-form/form/types';
import { GENDER } from '@/sections/forms/multistep-form/form/types';
import '@/components/gateway-forms/styles.css';
import { buildInitialValues } from './../initialValues';
import { createValidationSchema } from './../validation';
import { submitFormData } from './submission';
import {
  TERMIN_FORM_DEFAULT_VALUES,
  RECALL_TIME_OPTIONS,
  type TerminFormValues,
} from './consts';

const TerminForm: React.FC<TerminProps> = ({
  hiddenFields,
  spacingBottom,
  spacingTop,
  radioGroup,
  select,
  textInputs,
  sectionId,
  multistepConsultationTitle = '',
  multistepTopics = [],
  multistepAnliegen = '',
  onSubmitRef,
  onBeforeSubmit,
  onSuccess,
  onSubmitStart,
  onError,
  presetCallbackRequestNotes,
  presetWebsiteSelectedTopics,
}) => {
  const { t } = useTranslations();
  const privacyPolicyHref = t('gatewayForms.privacyPolicyUrl');
  const formikRef = useRef<FormikProps<TerminFormValues> | null>(null);

  useEffect(() => {
    if (!onSubmitRef) return;
    onSubmitRef(() => formikRef.current?.submitForm());
  }, [onSubmitRef]);

  const handleSubmit = async (
    values: TerminFormValues,
    { setSubmitting, resetForm }: FormikHelpers<TerminFormValues>,
  ) => {
    try {
      onSubmitStart?.();
      await onBeforeSubmit?.(values);

      const isSuccessful = await submitFormData(
        values,
        radioGroup,
        select,
        textInputs,
        {
          multistepMeta: {
            consultationTitle: multistepConsultationTitle,
            topics: multistepTopics,
            anliegen: multistepAnliegen,
          },
          presetCallbackRequestNotes,
          presetWebsiteSelectedTopics,
          hiddenFields,
        },
      );

      if (isSuccessful) {
        resetForm({ values: TERMIN_FORM_DEFAULT_VALUES });
        onSuccess?.();
        return;
      }
      onError?.();
    } catch {
      onError?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      id={sectionId}
      className={clsx('relative w-full', {
        'pt-4 md:pt-8 lg:pt-10': spacingTop,
        'pb-4 md:pb-8 lg:pb-20': spacingBottom,
      })}
    >
      <div className="w-full">
        <Formik<TerminFormValues>
          innerRef={(instance) => {
            formikRef.current = instance;
          }}
          initialValues={
            buildInitialValues(
              hiddenFields,
              TERMIN_FORM_DEFAULT_VALUES,
              radioGroup,
              select,
              textInputs,
            ) as TerminFormValues
          }
          enableReinitialize
          validationSchema={createValidationSchema(
            radioGroup || [],
            select || [],
            textInputs || [],
            t,
          )}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, values, errors, touched, setFieldTouched }) => (
            <Form className="space-y-6">
              {/* top selects */}
              {select?.map((selectItem, index) =>
                selectItem.position === 'top' ? (
                  <Field key={index} name={selectItem.name}>
                    {({ field, form, meta }: any) => (
                      <Select
                        name={selectItem.name}
                        label={selectItem.label}
                        options={selectItem.options}
                        value={field.value}
                        onChange={(selectedValue: string) =>
                          form.setFieldValue(selectItem.name, selectedValue)
                        }
                        placeholder={
                          selectItem.placeholder || 'Select an option'
                        }
                        errorMessage={
                          meta.touched && meta.error ? meta.error : undefined
                        }
                      />
                    )}
                  </Field>
                ) : null,
              )}

              {/* top radio groups */}
              {radioGroup?.map((radioItem, index) =>
                radioItem.position === 'top' ? (
                  <Field key={index} name={radioItem.name}>
                    {({ field, form, meta }: any) => (
                      <RadioGroup
                        name={radioItem.name}
                        label={radioItem.label}
                        options={radioItem.options}
                        value={field.value}
                        onChange={(selectedValue: string) =>
                          form.setFieldValue(radioItem.name, selectedValue)
                        }
                        spacingTop={radioItem.spacingTop}
                        spacingBottom={radioItem.spacingBottom}
                        errorMessage={
                          meta.touched && meta.error ? meta.error : undefined
                        }
                      />
                    )}
                  </Field>
                ) : null,
              )}

              {/* hidden fields */}
              {hiddenFields?.map((field, index) => (
                <Field
                  id={field.key}
                  key={index}
                  type="hidden"
                  name={field.key}
                  value={field.value}
                />
              ))}

              {/* Salutation */}
              <div>
                <label className="mb-1 text-sm font-medium text-neutral-600">
                  {t('gatewayForms.salutation')}
                </label>
                <div className="flex flex-row gap-6">
                  <Input
                    variant="light"
                    label={t('gatewayForms.mr')}
                    type="radio"
                    name="gender"
                    checked={values.gender === GENDER.MALE}
                    onChange={() => setFieldValue('gender', GENDER.MALE)}
                  />
                  <Input
                    variant="light"
                    label={t('gatewayForms.ms')}
                    type="radio"
                    name="gender"
                    checked={values.gender === GENDER.FEMALE}
                    onChange={() => setFieldValue('gender', GENDER.FEMALE)}
                  />
                </div>
                <ErrorMessage
                  name="gender"
                  component="div"
                  className="mt-1 text-sm text-red-600"
                />
              </div>

              {/* Name/Surname */}
              <div className="flex flex-col gap-6 md:flex-row">
                <div className="w-full md:w-1/2">
                  <Field name="name">
                    {({ field, meta }: any) => (
                      <Input
                        {...field}
                        variant="light"
                        label={t('gatewayForms.firstName')}
                        placeholder={t('form.firstNamePlaceholder')}
                        type="text"
                        error={Boolean(meta.touched && meta.error)}
                        onChange={(value) => setFieldValue('name', value)}
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="mt-1 text-sm text-red-600"
                  />
                </div>

                <div className="w-full md:w-1/2">
                  <Field name="surname">
                    {({ field, meta }: any) => (
                      <Input
                        {...field}
                        variant="light"
                        label={t('gatewayForms.lastName')}
                        placeholder={t('form.lastNamePlaceholder')}
                        type="text"
                        error={Boolean(meta.touched && meta.error)}
                        onChange={(value) => setFieldValue('surname', value)}
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name="surname"
                    component="div"
                    className="mt-1 text-sm text-red-600"
                  />
                </div>
              </div>

              {/* Phone / Email */}
              <div className="flex flex-col gap-6 md:flex-row">
                <div className="w-full md:w-1/2">
                  <label className="mb-1 block text-sm font-medium text-brand-700">
                    {t('header.termin.phoneLabel')}
                  </label>
                  <PhoneInput
                    inputClassName={`w-full py-6 ${
                      touched.phoneNumber && errors.phoneNumber ? 'invalid' : ''
                    }`}
                    defaultCountry="ch"
                    placeholder="000 000 00 00"
                    value={values.phoneNumber}
                    onChange={(phone) => setFieldValue('phoneNumber', phone)}
                    onBlur={() => setFieldTouched('phoneNumber', true)}
                  />
                  {touched.phoneNumber &&
                    typeof errors.phoneNumber === 'string' && (
                      <div className="mt-1 text-sm text-red-600">
                        {errors.phoneNumber}
                      </div>
                    )}
                </div>

                <div className="w-full md:w-1/2">
                  <Field name="email">
                    {({ field, meta }: any) => (
                      <Input
                        {...field}
                        variant="light"
                        label={t('header.termin.emailLabel')}
                        placeholder={t('header.termin.emailPlaceholder')}
                        type="email"
                        error={Boolean(meta.touched && meta.error)}
                        onChange={(value) => setFieldValue('email', value)}
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="mt-1 text-sm text-red-600"
                  />
                </div>
              </div>

              {/* Birth date */}
              <div className="pr-3 lg:w-1/2">
                <Field name="birthDate">
                  {({ field, meta }: any) => (
                    <Input
                      {...field}
                      variant="light"
                      label={t('gatewayForms.birthDate')}
                      placeholder={t('gatewayForms.birthDate')}
                      max={new Date().toISOString().split('T')[0]}
                      type="date"
                      error={Boolean(meta.touched && meta.error)}
                      onChange={(value) => setFieldValue('birthDate', value)}
                    />
                  )}
                </Field>
                <ErrorMessage
                  name="birthDate"
                  component="div"
                  className="mt-1 text-sm text-red-600"
                />
              </div>
              <div className="mb-4 flex items-center gap-4">
                <span className="text-[12px] font-medium text-neutral-200">
                  {t('gatewayForms.callback')}
                </span>
                <div className="h-px w-full bg-neutral-200/80" />
              </div>
              {/* recall date/time */}
              <div className="flex flex-col gap-6 md:flex-row">
                <div className="w-full md:w-1/2">
                  <Field name="recallDate">
                    {({ field, meta }: any) => (
                      <Input
                        {...field}
                        variant="light"
                        label={t('header.termin.recallDate')}
                        placeholder="Datum für Rückruf"
                        type="date"
                        min={new Date().toISOString().split('T')[0]}
                        error={Boolean(meta.touched && meta.error)}
                        onChange={(value) => setFieldValue('recallDate', value)}
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name="recallDate"
                    component="div"
                    className="mt-1 text-sm text-red-600"
                  />
                </div>

                <div className="w-full">
                  <Field name="recallTime">
                    {() => {
                      const hasError = Boolean(
                        touched.recallTime && errors.recallTime,
                      );

                      return (
                        <div>
                          <label className="block text-sm font-medium text-brand-700">
                            {t('header.termin.recallTime')}
                          </label>

                          <div
                            role="radiogroup"
                            aria-label={t('header.termin.recallTime')}
                            className="mt-2 flex flex-wrap gap-3"
                          >
                            {RECALL_TIME_OPTIONS.map((opt) => {
                              const isActive = values.recallTime === opt.value;

                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  role="radio"
                                  aria-checked={isActive}
                                  onClick={() =>
                                    setFieldValue('recallTime', opt.value)
                                  }
                                  className={clsx(
                                    'rounded-full border px-4 py-2 text-sm font-medium transition-colors',
                                    isActive
                                      ? 'border-brand-700 bg-brand-700/10 text-brand-700'
                                      : 'border-neutral-200 bg-white text-slate-700 hover:border-brand-300',
                                    hasError ? 'border-red-600' : '',
                                  )}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>

                          <input
                            type="hidden"
                            name="recallTime"
                            value={values.recallTime || ''}
                          />

                          {hasError &&
                            typeof errors.recallTime === 'string' && (
                              <div className="mt-1 text-sm text-red-600">
                                {errors.recallTime}
                              </div>
                            )}
                        </div>
                      );
                    }}
                  </Field>
                </div>
              </div>

              {/* ✅ Terms */}
              <div className="flex flex-col gap-1">
                <Field name="termsAndConditions">
                  {({ field, meta }: any) => (
                    <label className="flex w-full min-w-0 items-start text-brand-700">
                      <input
                        name={field.name}
                        type="checkbox"
                        checked={Boolean(field.value)}
                        onBlur={field.onBlur}
                        onChange={() =>
                          setFieldValue('termsAndConditions', !field.value)
                        }
                        className="mt-1 !h-4 !w-4 flex-shrink-0 !scale-100 accent-brand-700"
                      />
                      <span className="ml-3 min-w-0 flex-1 break-words">
                        {t('gatewayForms.termsPrefix')}
                        <a
                          href={privacyPolicyHref}
                          className="underline underline-offset-4"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('gatewayForms.termsLink')}
                        </a>
                        {t('gatewayForms.termsSuffix')}
                      </span>
                    </label>
                  )}
                </Field>
                <ErrorMessage
                  name="termsAndConditions"
                  component="div"
                  className="text-sm text-red-600"
                />
              </div>

              {/* bottom selects */}
              {select?.map((selectItem, index) =>
                selectItem.position === 'bottom' ? (
                  <Field key={index} name={selectItem.name}>
                    {({ field, form, meta }: any) => (
                      <Select
                        name={selectItem.name}
                        label={selectItem.label}
                        options={selectItem.options}
                        value={field.value}
                        onChange={(selectedValue: string) =>
                          form.setFieldValue(selectItem.name, selectedValue)
                        }
                        placeholder={
                          selectItem.placeholder || 'Select an option'
                        }
                        errorMessage={
                          meta.touched && meta.error ? meta.error : undefined
                        }
                      />
                    )}
                  </Field>
                ) : null,
              )}

              {/* bottom radio groups */}
              {radioGroup?.map((radioItem, index) =>
                radioItem.position === 'bottom' ? (
                  <Field key={index} name={radioItem.name}>
                    {({ field, form, meta }: any) => (
                      <RadioGroup
                        name={radioItem.name}
                        label={radioItem.label}
                        options={radioItem.options}
                        value={field.value}
                        onChange={(selectedValue: string) =>
                          form.setFieldValue(radioItem.name, selectedValue)
                        }
                        spacingTop={radioItem.spacingTop}
                        spacingBottom={radioItem.spacingBottom}
                        errorMessage={
                          meta.touched && meta.error ? meta.error : undefined
                        }
                      />
                    )}
                  </Field>
                ) : null,
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default TerminForm;
